import { authFetch, authHeaders, resolveApiBaseUrl } from './auth';

const API_BASE_URL = resolveApiBaseUrl();

const DEFAULT_REPLY_TO = [
  'support@artstoheartsproject.com',
  'Orders@artstoheartsproject.com',
];

const needHelpEmailsHtml = () =>
  DEFAULT_REPLY_TO.map(
    (email) =>
      `<a href="mailto:${email}" style="color: #667eea; text-decoration: none;">${email}</a>`
  ).join('<br>');

const getTrackingLink = (trackingNumber = '') => {
  const tn = String(trackingNumber || '').trim();
  return tn
    ? `https://ae.speedboxapp.com/track-shipment?trackingNumber=${encodeURIComponent(tn)}`
    : 'https://ae.speedboxapp.com/track-shipment?trackingNumber';
};

const trackingLinkHtml = (link) =>
  `<a href="${link}" style="color: #1976d2; text-decoration: underline;">${link}</a>`;

const warmlyHtml = 'Warmly,<br>Team Arts to Hearts Project';
const warmlyText = 'Warmly,\nTeam Arts to Hearts Project';

const normalizeStatusKey = (status) =>
  String(status || '').toLowerCase().replace(/-/g, '_').trim();

const orderWithHash = (orderDetails) => {
  const orderName = String(orderDetails?.order_name || '').trim();
  if (!orderName) return '#';
  return orderName.startsWith('#') ? orderName : `#${orderName}`;
};

const getStatusCopy = (status, trackingNumber = '') => {
  const link = getTrackingLink(trackingNumber);
  const linkHtml = trackingLinkHtml(link);
  const key = normalizeStatusKey(status);

  const copy = {
    unfulfilled: {
      subject: (o) => `Your Order is Placed ${orderWithHash(o)}`,
      title: 'Your Order is Placed',
      emoji: '',
      color: '#ff5722',
      messageHtml:
        `Thank you for your order!<br><br>` +
        `Your order has been placed successfully and our team has started processing it.<br><br>` +
        `If your order contains a preorder item, we'll keep you updated throughout the production and shipping process so you always know what's happening.<br><br>` +
        `We'll send you another email as soon as your order reaches the next stage.<br><br>` +
        `Thank you for supporting the Arts to Hearts Project.<br><br>${warmlyHtml}`,
      messageText:
        `Thank you for your order!\n\n` +
        `Your order has been placed successfully and our team has started processing it.\n\n` +
        `If your order contains a preorder item, we'll keep you updated throughout the production and shipping process so you always know what's happening.\n\n` +
        `We'll send you another email as soon as your order reaches the next stage.\n\n` +
        `Thank you for supporting the Arts to Hearts Project.\n\n${warmlyText}`,
    },
    preorder_received: {
      subject: (o) => `Your Preorder is Placed – Order ${orderWithHash(o)}`,
      title: 'Your Preorder is Placed',
      emoji: '',
      color: '#7b1fa2',
      messageHtml:
        `Thank you for placing your preorder.<br><br>` +
        `Your copy has been reserved, and we're currently working with our production partners to bring this publication to life.<br><br>` +
        `We expect stock to arrive at our warehouse in approximately 15 days. As soon as it arrives, we'll begin preparing your order for shipment and keep you informed every step of the way.<br><br>` +
        `Thank you for your patience and for supporting independent publishing.<br><br>${warmlyHtml}`,
      messageText:
        `Thank you for placing your preorder.\n\n` +
        `Your copy has been reserved, and we're currently working with our production partners to bring this publication to life.\n\n` +
        `We expect stock to arrive at our warehouse in approximately 15 days. As soon as it arrives, we'll begin preparing your order for shipment and keep you informed every step of the way.\n\n` +
        `Thank you for your patience and for supporting independent publishing.\n\n${warmlyText}`,
    },
    progress: {
      subject: (o) => `Your Book is Currently in Production – Order ${orderWithHash(o)}`,
      title: 'Your Book is Currently in Production',
      emoji: '',
      color: '#ff9800',
      messageHtml:
        `We wanted to share a quick update on your preorder.<br><br>` +
        `Your book is currently in production and everything is progressing as planned. Once the books arrive at our warehouse, we'll begin packing your order immediately.<br><br>` +
        `We'll send you another update as soon as stock arrives.<br><br>` +
        `Thank you for your continued patience and support.<br><br>${warmlyHtml}`,
      messageText:
        `We wanted to share a quick update on your preorder.\n\n` +
        `Your book is currently in production and everything is progressing as planned. Once the books arrive at our warehouse, we'll begin packing your order immediately.\n\n` +
        `We'll send you another update as soon as stock arrives.\n\n` +
        `Thank you for your continued patience and support.\n\n${warmlyText}`,
    },
    delay: {
      subject: (o) => `A Quick Update About Your Preorder – Order ${orderWithHash(o)}`,
      title: 'A Quick Update About Your Preorder',
      emoji: '',
      color: '#f57c00',
      messageHtml:
        `We wanted to keep you informed about your preorder.<br><br>` +
        `Your book is taking a little longer than originally expected to reach our warehouse. Your order remains fully confirmed, and your copy has been reserved.<br><br>` +
        `As soon as stock arrives, we'll begin packing your order and notify you immediately.<br><br>` +
        `We sincerely appreciate your patience and understanding. Thank you for supporting a small independent publishing platform.<br><br>${warmlyHtml}`,
      messageText:
        `We wanted to keep you informed about your preorder.\n\n` +
        `Your book is taking a little longer than originally expected to reach our warehouse. Your order remains fully confirmed, and your copy has been reserved.\n\n` +
        `As soon as stock arrives, we'll begin packing your order and notify you immediately.\n\n` +
        `We sincerely appreciate your patience and understanding. Thank you for supporting a small independent publishing platform.\n\n${warmlyText}`,
    },
    stock_in: {
      subject: (o) => `Great News! Your Book Has Arrived – Order ${orderWithHash(o)}`,
      title: 'Great News! Your Book Has Arrived',
      emoji: '',
      color: '#2e7d32',
      messageHtml:
        `Great news!<br><br>` +
        `Your book has arrived at our warehouse and our fulfillment team is now preparing your order for shipment.<br><br>` +
        `You'll receive another email with your tracking details as soon as your package is dispatched.<br><br>` +
        `We're excited to get your book into your hands!<br><br>${warmlyHtml}`,
      messageText:
        `Great news!\n\n` +
        `Your book has arrived at our warehouse and our fulfillment team is now preparing your order for shipment.\n\n` +
        `You'll receive another email with your tracking details as soon as your package is dispatched.\n\n` +
        `We're excited to get your book into your hands!\n\n${warmlyText}`,
    },
    packing: {
      subject: (o) => `We're Packing Your Order – Order ${orderWithHash(o)}`,
      title: "We're Packing Your Order",
      emoji: '',
      color: '#ff9800',
      messageHtml:
        `Your order is now being carefully packed and prepared for shipment.<br><br>` +
        `We'll send your tracking information as soon as your package leaves our warehouse.<br><br>` +
        `Thank you for your patience—we're almost there!<br><br>${warmlyHtml}`,
      messageText:
        `Your order is now being carefully packed and prepared for shipment.\n\n` +
        `We'll send your tracking information as soon as your package leaves our warehouse.\n\n` +
        `Thank you for your patience—we're almost there!\n\n${warmlyText}`,
    },
    shipping_domestic: {
      subject: (o) => `Your Order Has Been Shipped – Order ${orderWithHash(o)}`,
      title: 'Your Order Has Been Shipped',
      emoji: '',
      color: '#2196f3',
      messageHtml:
        `Your order is officially on its way!<br><br>` +
        `Your estimated delivery time is 3–5 business days.<br><br>` +
        `You can track your shipment here:<br>${linkHtml}<br><br>` +
        `We hope you enjoy your book!<br><br>${warmlyHtml}`,
      messageText:
        `Your order is officially on its way!\n\n` +
        `Your estimated delivery time is 3–5 business days.\n\n` +
        `You can track your shipment here:\n${link}\n\n` +
        `We hope you enjoy your book!\n\n${warmlyText}`,
    },
    shipping_international: {
      subject: (o) => `Your Order Has Been Shipped – Order ${orderWithHash(o)}`,
      title: 'Your Order Has Been Shipped',
      emoji: '',
      color: '#2196f3',
      messageHtml:
        `Your order has been shipped and is now on its way to you.<br><br>` +
        `International deliveries generally arrive within 7–15 business days, depending on customs and your local courier.<br><br>` +
        `Track your shipment here:<br>${linkHtml}<br><br>` +
        `Thank you for your patience.<br><br>${warmlyHtml}`,
      messageText:
        `Your order has been shipped and is now on its way to you.\n\n` +
        `International deliveries generally arrive within 7–15 business days, depending on customs and your local courier.\n\n` +
        `Track your shipment here:\n${link}\n\n` +
        `Thank you for your patience.\n\n${warmlyText}`,
    },
    arrived_at_destination: {
      subject: (o) => `Your Package Has Arrived in Your Country – Order ${orderWithHash(o)}`,
      title: 'Your Package Has Arrived in Your Country',
      emoji: '',
      color: '#1976d2',
      messageHtml:
        `Good news!<br><br>` +
        `Your package has arrived in the destination country and is now with the local delivery network for the final stage of its journey.<br><br>` +
        `Track your shipment here:<br>${linkHtml}<br><br>` +
        `Your order should be with you soon.<br><br>${warmlyHtml}`,
      messageText:
        `Good news!\n\n` +
        `Your package has arrived in the destination country and is now with the local delivery network for the final stage of its journey.\n\n` +
        `Track your shipment here:\n${link}\n\n` +
        `Your order should be with you soon.\n\n${warmlyText}`,
    },
    destoryed: {
      subject: (o) => `Your Shipment Has Cleared Customs – Order ${orderWithHash(o)}`,
      title: 'Your Shipment Has Cleared Customs',
      emoji: '',
      color: '#388e3c',
      messageHtml:
        `Your shipment has successfully cleared customs and is now moving through your local courier's network.<br><br>` +
        `You can continue tracking your package here:<br>${linkHtml}<br><br>` +
        `We're almost at the finish line!<br><br>${warmlyHtml}`,
      messageText:
        `Your shipment has successfully cleared customs and is now moving through your local courier's network.\n\n` +
        `You can continue tracking your package here:\n${link}\n\n` +
        `We're almost at the finish line!\n\n${warmlyText}`,
    },
    out_of_delivery: {
      subject: (o) => `Your Order is Out for Delivery – Order ${orderWithHash(o)}`,
      title: 'Your Order is Out for Delivery',
      emoji: '',
      color: '#0288d1',
      messageHtml:
        `Today's the day!<br><br>` +
        `Your package is out for delivery and should arrive very soon.<br><br>` +
        `Track your shipment here:<br>${linkHtml}<br><br>` +
        `We hope you love your new book.<br><br>${warmlyHtml}`,
      messageText:
        `Today's the day!\n\n` +
        `Your package is out for delivery and should arrive very soon.\n\n` +
        `Track your shipment here:\n${link}\n\n` +
        `We hope you love your new book.\n\n${warmlyText}`,
    },
    delivered: {
      subject: (o) => `Your Order Has Been Delivered – Order ${orderWithHash(o)}`,
      title: 'Your Order Has Been Delivered',
      emoji: '',
      color: '#4caf50',
      messageHtml:
        `According to our courier, your order has been successfully delivered.<br><br>` +
        `We hope you enjoy your book and that it becomes a meaningful addition to your collection.<br><br>` +
        `Thank you for supporting the Arts to Hearts Project.<br><br>${warmlyHtml}`,
      messageText:
        `According to our courier, your order has been successfully delivered.\n\n` +
        `We hope you enjoy your book and that it becomes a meaningful addition to your collection.\n\n` +
        `Thank you for supporting the Arts to Hearts Project.\n\n${warmlyText}`,
    },
    review: {
      subject: () => 'How Did We Do?',
      title: 'How Did We Do?',
      emoji: '',
      color: '#ff9800',
      messageHtml:
        `We hope you've had a chance to enjoy your new book.<br><br>` +
        `As a small independent publishing platform, every order and every review helps us continue creating opportunities for artists around the world.<br><br>` +
        `If you enjoyed your experience, we'd be incredibly grateful if you could leave us a quick Google review.<br><br>` +
        `Leave your review here:<br>` +
        `<a href="https://g.page/r/CV5shcA9rDZQEAE/review" style="display: inline-block; padding: 10px 20px; background-color: #4CAF50; color: white; text-decoration: none; border-radius: 5px; font-weight: bold;">Leave Your Review Here</a><br><br>` +
        `Thank you for being part of our community and for supporting the Arts to Hearts Project.<br><br>${warmlyHtml}`,
      messageText:
        `We hope you've had a chance to enjoy your new book.\n\n` +
        `As a small independent publishing platform, every order and every review helps us continue creating opportunities for artists around the world.\n\n` +
        `If you enjoyed your experience, we'd be incredibly grateful if you could leave us a quick Google review.\n\n` +
        `Leave your review here:\nhttps://g.page/r/CV5shcA9rDZQEAE/review\n\n` +
        `Thank you for being part of our community and for supporting the Arts to Hearts Project.\n\n${warmlyText}`,
    },
    dispute: {
      subject: (o) => `We've Received Your Request – Order ${orderWithHash(o)}`,
      title: "We've Received Your Request",
      emoji: '',
      color: '#ff5722',
      messageHtml:
        `Thank you for letting us know about the issue with your order.<br><br>` +
        `We're sorry for the inconvenience.<br><br>` +
        `Our team is reviewing your request and will update you shortly with the next steps.<br><br>` +
        `Thank you for your patience.<br><br>${warmlyHtml}`,
      messageText:
        `Thank you for letting us know about the issue with your order.\n\n` +
        `We're sorry for the inconvenience.\n\n` +
        `Our team is reviewing your request and will update you shortly with the next steps.\n\n` +
        `Thank you for your patience.\n\n${warmlyText}`,
    },
    replacement_approved: {
      subject: (o) => `Your Replacement Has Been Approved – Order ${orderWithHash(o)}`,
      title: 'Your Replacement Has Been Approved',
      emoji: '',
      color: '#6a1b9a',
      messageHtml:
        `We've approved your replacement request.<br><br>` +
        `Our team is now preparing a new copy for shipment. We'll send you another email as soon as it's on its way.<br><br>` +
        `Thank you for your patience.<br><br>${warmlyHtml}`,
      messageText:
        `We've approved your replacement request.\n\n` +
        `Our team is now preparing a new copy for shipment. We'll send you another email as soon as it's on its way.\n\n` +
        `Thank you for your patience.\n\n${warmlyText}`,
    },
    replacement_packing: {
      subject: (o) => `Your Replacement is Being Packed – Order ${orderWithHash(o)}`,
      title: 'Your Replacement is Being Packed',
      emoji: '',
      color: '#ff9800',
      messageHtml:
        `Your replacement copy is now being carefully packed.<br><br>` +
        `We'll send you the tracking details as soon as it ships.<br><br>${warmlyHtml}`,
      messageText:
        `Your replacement copy is now being carefully packed.\n\n` +
        `We'll send you the tracking details as soon as it ships.\n\n${warmlyText}`,
    },
    replacement_shipping_domestic: {
      subject: (o) => `Your Replacement Has Been Shipped – Order ${orderWithHash(o)}`,
      title: 'Your Replacement Has Been Shipped',
      emoji: '',
      color: '#2196f3',
      messageHtml:
        `Your replacement copy is now on its way.<br><br>` +
        `You can track it here:<br>${linkHtml}<br><br>` +
        `We hope it reaches you soon.<br><br>${warmlyHtml}`,
      messageText:
        `Your replacement copy is now on its way.\n\n` +
        `You can track it here:\n${link}\n\n` +
        `We hope it reaches you soon.\n\n${warmlyText}`,
    },
    replacement_shipping_international: {
      subject: (o) => `Your Replacement Has Been Shipped – Order ${orderWithHash(o)}`,
      title: 'Your Replacement Has Been Shipped',
      emoji: '',
      color: '#2196f3',
      messageHtml:
        `Your replacement copy is now on its way.<br><br>` +
        `You can track it here:<br>${linkHtml}<br><br>` +
        `We hope it reaches you soon.<br><br>${warmlyHtml}`,
      messageText:
        `Your replacement copy is now on its way.\n\n` +
        `You can track it here:\n${link}\n\n` +
        `We hope it reaches you soon.\n\n${warmlyText}`,
    },
    replacement_delivered: {
      subject: (o) => `Your Replacement Has Been Delivered – Order ${orderWithHash(o)}`,
      title: 'Your Replacement Has Been Delivered',
      emoji: '',
      color: '#4caf50',
      messageHtml:
        `Your replacement copy has been delivered.<br><br>` +
        `We hope everything arrived safely and that this resolves the issue. Thank you for giving us the opportunity to make things right.<br><br>${warmlyHtml}`,
      messageText:
        `Your replacement copy has been delivered.\n\n` +
        `We hope everything arrived safely and that this resolves the issue. Thank you for giving us the opportunity to make things right.\n\n${warmlyText}`,
    },
    refunded: {
      subject: (o) => `Your Refund Has Been Processed – Order ${orderWithHash(o)}`,
      title: 'Your Refund Has Been Processed',
      emoji: '',
      color: '#4caf50',
      messageHtml:
        `Your refund has been successfully initiated.<br><br>` +
        `Depending on your payment provider or bank, the amount should appear in your original payment method within 5–7 business days.<br><br>` +
        `Thank you for your patience, and we're sorry we couldn't meet your expectations this time.<br><br>` +
        `We hope to serve you again in the future.<br><br>${warmlyHtml}`,
      messageText:
        `Your refund has been successfully initiated.\n\n` +
        `Depending on your payment provider or bank, the amount should appear in your original payment method within 5–7 business days.\n\n` +
        `Thank you for your patience, and we're sorry we couldn't meet your expectations this time.\n\n` +
        `We hope to serve you again in the future.\n\n${warmlyText}`,
    },
  };

  // Aliases
  if (key === 'packing_started' || status === 'Packing Started') return copy.packing;
  if (key === 'preorder_delay') return copy.delay;
  if (key === 'shipping') return copy.shipping_domestic;

  return (
    copy[key] || {
      subject: (o) => `Order Update – Order ${orderWithHash(o)}`,
      title: 'Order Update',
      emoji: '',
      color: '#2196f3',
      messageHtml: `There's been an update to your order status. Please check your account for more details.<br><br>${warmlyHtml}`,
      messageText: `There's been an update to your order status. Please check your account for more details.\n\n${warmlyText}`,
    }
  );
};

// Email Templates
const getEmailTemplate = (status, orderDetails, trackingNumber = '') => {
  const { name, order_name, line_items } = orderDetails;
  const copy = getStatusCopy(status, trackingNumber);
  const statusInfo = {
    title: copy.title,
    greeting: 'Hi',
    message: copy.messageHtml,
    color: copy.color,
    emoji: copy.emoji,
  };
  
  const baseStyle = `
    font-family: Arial, sans-serif;
    max-width: 600px;
    margin: 0 auto;
    background-color: #f8f9fa;
    padding: 20px;
  `;

  const cardStyle = `
    background: white;
    border-radius: 10px;
    padding: 30px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  `;

  
  // Calculate total amount
  const totalAmount = line_items?.reduce((sum, item) => {
    return sum + (parseFloat(item.price) * item.quantity);
  }, 0) || 0;

  const formatPrice = (price) => {
    const numPrice = parseFloat(price) || 0;
    return `$${numPrice.toFixed(2)}`;
  };

  // Generate order items HTML
  const orderItemsHtml = line_items?.map(item => {
    const itemTotal = parseFloat(item.price) * item.quantity;
    const productUrl = `https://artstoheartsproject.myshopify.com/products/${item.product_handle || item.title?.toLowerCase().replace(/\s+/g, '-')}`;
    
    return `
    <div style="background: #f8f9fa; padding: 10px; margin: 5px 0; border-radius: 5px; border-left: 3px solid ${statusInfo.color};">
      <a href="${productUrl}" style="text-decoration: none; color: #2c3e50;">
        <strong style="color: #2c3e50; text-decoration: underline;">${item.title}</strong>
      </a><br>
      <small>Quantity: ${item.quantity} - Price: ${formatPrice(itemTotal)}</small>
    </div>`;
  }).join('') || '<div style="padding: 10px;">No items found</div>';

  // Tracking number box + Speedbox block: ONLY when order is shipped
  // (domestic / international / replacement shipping) — not on other status emails.
  const normalizedStatus = String(status || '').toLowerCase().replace(/-/g, '_').trim();
  const isOrderShippedStatus = [
    'shipping_domestic',
    'shipping_international',
    'arrived_at_destination',
    'replacement_shipping_domestic',
    'replacement_shipping_international',
    'shipping domestic',
    'shipping international',
  ].includes(normalizedStatus);
  const speedboxTrackingUrl = getTrackingLink(trackingNumber);

  const trackingSection = (isOrderShippedStatus && trackingNumber) ? `
    <div style="background: #e3f2fd; padding: 15px; border-radius: 8px; margin: 20px 0; border-left: 4px solid ${statusInfo.color};">
      <h3 style="margin: 0 0 10px 0; color: #1976d2;">Tracking Information</h3>
      <p style="margin: 0; font-weight: bold; color: #1976d2;">Tracking Number: ${trackingNumber}</p>
      <p style="margin: 5px 0 0 0; font-size: 14px; color: #666;">You can track your order using this tracking number.</p>
    </div>
  ` : '';

  const speedboxTrackingSection = isOrderShippedStatus ? `
    <div style="background: #f3f8ff; padding: 15px; border-radius: 8px; margin: 20px 0; border-left: 4px solid ${statusInfo.color};">
      <h3 style="margin: 0 0 10px 0; color: #1976d2;">Track on Speedbox</h3>
      <p style="margin: 0; font-size: 14px; color: #333;">
        <a href="${speedboxTrackingUrl}" style="color: #1976d2; text-decoration: underline;">
          ${speedboxTrackingUrl}
        </a>
      </p>
    </div>
  ` : '';

  return `
    <div style="${baseStyle}">
      <div style="${cardStyle}">
        <div style="text-align: center; margin-bottom: 30px;">
          <h1 style="color: ${statusInfo.color}; margin: 0;">${statusInfo.title}</h1>
        </div>
        
        <div style="background: #f8f9fa; padding: 20px; border-radius: 8px; margin-bottom: 25px;">
          <p style="margin: 0; font-size: 16px; color: #333; line-height: 1.6;">
            ${statusInfo.greeting || 'Dear'} ${name || 'Valued Customer'},
          </p>
          <p style="margin: 15px 0 0 0; font-size: 16px; color: #333; line-height: 1.6;">
            ${statusInfo.message}
          </p>
        </div>

        <div style="margin-bottom: 25px;">
          <h3 style="color: #333; border-bottom: 2px solid ${statusInfo.color}; padding-bottom: 10px;">
            Order Details - ${order_name}
          </h3>
          ${orderItemsHtml}
        </div>

        ${trackingSection}
        ${speedboxTrackingSection}

        <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 20px; border-radius: 8px; text-align: center; margin-top: 30px;">
          <h3 style="margin: 0 0 10px 0;">Thank you for your business!</h3>
          <p style="margin: 0; opacity: 0.9;">If you have any questions, feel free to contact our support team.</p>
        </div>

        <div style="background: #f8f9fa; padding: 20px; border-radius: 8px; margin-top: 30px; text-align: center;">
          <h3 style="margin: 0 0 15px 0; color: #2c3e50;">Need Help?</h3>
          <p style="margin: 0 0 10px 0; color: #666; font-size: 14px;">
            Feel free to contact us for any questions or concerns
          </p>
          <p style="margin: 0; color: #667eea; font-size: 16px; font-weight: bold; line-height: 1.8;">
            ${needHelpEmailsHtml()}
          </p>
        </div>

        <div style="text-align: center; margin-top: 30px; padding-top: 20px; border-top: 1px solid #eee;">
          <p style="margin: 0; color: #666; font-size: 14px;">
            Best regards,<br>
            <strong>Arts To Hearts project Team</strong>
          </p>
        </div>
      </div>
    </div>
  `;
};

// Generate plain text version of email
const getTextTemplate = (status, orderDetails, trackingNumber = '') => {
  const { name, order_name, line_items } = orderDetails;
  const copy = getStatusCopy(status, trackingNumber);

  const orderItemsText = line_items?.map(item => {
    const itemTotal = parseFloat(item.price) * item.quantity;
    return `- ${item.title} (Qty: ${item.quantity}) - $${itemTotal.toFixed(2)}`;
  }).join('\n') || 'No items found';

  return `Hi ${name || 'Valued Customer'},

${copy.messageText}

Order Details - ${order_name}
${orderItemsText}

Need help? Contact us: ${DEFAULT_REPLY_TO.join(', ')}`;
};

// 🔒 SECURE EMAIL SERVICE - All credentials handled server-side
const emailService = {
  
  // Compatibility stubs (avoid runtime errors if UI calls these)
  setServiceType: (type) => { emailService._serviceType = type; },
  // SMTP/SES credentials live on the Django backend only.
  configureSES: () => {},
  configureSMTP: () => {},
  configure: (config) => { emailService._config = config; },

  // 🔒 SECURE: Send status update email via backend API (no credentials exposed)
  sendStatusUpdateEmail: async (status, orderDetails, trackingNumber = '') => {
    try {
      const subject = getEmailSubject(status, orderDetails);
      const htmlContent = getEmailTemplate(status, orderDetails, trackingNumber);
      const textContent = getTextTemplate(status, orderDetails, trackingNumber);

      // 🔒 SECURE: Send email via backend API without any credentials
      const emailData = {
        to: orderDetails.email,
        subject: subject,
        html: htmlContent,
        text: textContent,
        reply_to: DEFAULT_REPLY_TO
      };

      
      const response = await authFetch(`${API_BASE_URL}/send-email/`, {
        method: 'POST',
        headers: authHeaders({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify(emailData)
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Email sending failed');
      }

      
      return {
        success: true,
        messageId: result.message_id,
        message: result.message,
        service: result.service
      };

    } catch (error) {
      console.error('❌ Secure email sending failed:', error);
      
      throw new Error(`Email sending failed: ${error.message}`);
    }
  },

  // Build preview content for UI (without sending).
  buildStatusEmailContent: (status, orderDetails, trackingNumber = '') => {
    const subject = getEmailSubject(status, orderDetails);
    const html = getEmailTemplate(status, orderDetails, trackingNumber);
    const text = getTextTemplate(status, orderDetails, trackingNumber);
    return { subject, html, text };
  },

  // 🔒 SECURE: Test email configuration via backend
  testConfiguration: async () => {
    try {
      const testEmailData = {
        to: 'amitkumarnbd9900@gmail.com',
        subject: '🔒 Secure Email Service Test',
        html: `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
            <div style="background: white; border-radius: 10px; padding: 30px; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">
              <h1 style="color: #4caf50; text-align: center;">🔒 Email Service Test</h1>
              <p>This is a test email to verify that the secure email service is working correctly.</p>
              <p><strong>✅ Security Status:</strong> All credentials are safely stored on the server.</p>
              <p><strong>📅 Test Time:</strong> ${new Date().toISOString()}</p>
            </div>
          </div>
        `,
        text: 'Email Service Test - This is a test email to verify that the secure email service is working correctly.',
        reply_to: DEFAULT_REPLY_TO
      };

      const response = await authFetch(`${API_BASE_URL}/send-email/`, {
        method: 'POST',
        headers: authHeaders({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify(testEmailData)
      });

      const result = await response.json();

      if (!response.ok) {
        return {
          success: false,
          message: result.error || 'Email configuration test failed'
        };
      }

      return {
        success: true,
        messageId: result.message_id,
        message: 'Email configuration is working correctly',
        service: result.service
      };

    } catch (error) {
      return {
        success: false,
        message: `Email configuration test failed: ${error.message}`
      };
    }
  }
};

// Helper functions
const getEmailSubject = (status, orderDetails) => {
  const copy = getStatusCopy(status);
  return copy.subject(orderDetails);
};

const getStatusEmoji = (status) => '';

const getStatusText = (status) => {
  const copy = getStatusCopy(status);
  return copy.title || 'Status Update';
};

export default emailService; 
